import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '@/hooks/useCart';
import { useMember } from '@/hooks/useMember';
import { changePassword as changePasswordApi, logout as logoutApi } from '@/api/auth';
import { canAccessPurchasePlan, clearAuth, getDepartmentQuota, getUser, isDivideAccount, redirectToLogin } from '@/utils/auth';
import { alertError, alertSuccess } from '@/utils/message';
import ChangePasswordModal from '@/components/feature/ChangePasswordModal';
import UsageRatioBar from '@/components/base/UsageRatioBar';

interface NavItem {
  path: string;
  label: string;
  icon: string;
}

interface NavGroup {
  label: string;
  icon: string;
  items: NavItem[];
}

const purchaseGroup: NavGroup = {
  label: '采购管理',
  icon: 'ri-shopping-bag-line',
  items: [
    { path: '/orders', label: '订单管理', icon: 'ri-file-list-3-line' },
    { path: '/purchase-plan', label: '采购计划', icon: 'ri-calendar-check-line' },
    { path: '/after-sales', label: '我的售后', icon: 'ri-customer-service-line' },
    { path: '/proxy-order', label: '代购下单', icon: 'ri-user-received-line' },
    { path: '/frequent-purchase', label: '常购单', icon: 'ri-heart-3-line' },
  ],
};

const cartNavItem: NavItem = {
  path: '/cart',
  label: '购物车',
  icon: 'ri-shopping-cart-line',
};

type SearchType = 'goods' | 'supplier';

const SEARCH_TYPE_OPTIONS: { value: SearchType; label: string; placeholder: string }[] = [
  { value: 'goods', label: '物资', placeholder: '搜索物资...' },
  { value: 'supplier', label: '供应商', placeholder: '搜索供应商...' },
];

const topNavItems: NavItem[] = [
  { path: '/goods', label: '物资浏览', icon: 'ri-store-2-line' },
  { path: '/reports', label: '数据报表', icon: 'ri-bar-chart-2-line' },
];

/** 工作审批菜单（暂未启用） */
const workApprovalItem: NavItem | null = null;
// const workApprovalItem: NavItem = {
//   path: '/work-approval',
//   label: '工作审批',
//   icon: 'ri-shield-check-line',
// };

function Badge({ count, color = 'red' }: { count: number; color?: 'red' | 'blue' }) {
  if (count <= 0) return null;
  return (
    <span
      className={`min-w-[16px] h-4 px-1 rounded-full text-white text-[10px] leading-4 text-center font-medium ${
        color === 'blue' ? 'bg-blue-500' : 'bg-red-500'
      }`}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { totalCount } = useCart();
  const {
    member: currentUser,
    loading: userLoading,
    flowsCount,
    purchasePlansCount,
    clear: clearMember,
  } = useMember();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState<SearchType>('goods');
  const [searchTypeOpen, setSearchTypeOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const userMenuRef = useRef<HTMLDivElement>(null);
  const searchTypeRef = useRef<HTMLDivElement>(null);
  const activeSearchType =
    SEARCH_TYPE_OPTIONS.find((opt) => opt.value === searchType) || SEARCH_TYPE_OPTIONS[0];

  const displayName = currentUser?.nickname || currentUser?.account || (userLoading ? '加载中...' : '用户');
  const displayDept =
    currentUser?.department?.title ||
    currentUser?.enterprise?.title ||
    currentUser?.position ||
    '';
  const showProxyOrder = isDivideAccount(currentUser ?? getUser());
  const showPurchasePlan = canAccessPurchasePlan(currentUser ?? getUser());
  const deptQuota = getDepartmentQuota(currentUser ?? getUser());

  const formatMoney = (n: number) =>
    `¥${n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const visiblePurchaseGroup: NavGroup = {
    ...purchaseGroup,
    items: purchaseGroup.items.filter((item) => {
      if (item.path === '/proxy-order') return showProxyOrder;
      if (item.path === '/purchase-plan') return showPurchasePlan;
      return true;
    }),
  };

  const itemBadge = (path: string) => {
    if (path === '/orders') return flowsCount;
    if (path === '/purchase-plan') return purchasePlansCount;
    if (path === '/cart') return totalCount;
    return 0;
  };

  /** 采购管理入口徽标：订单待办 + 采购计划待办（无采购计划权限时不计计划数） */
  const purchaseGroupBadge =
    flowsCount + (showPurchasePlan ? purchasePlansCount : 0);

  const handleLogout = async () => {
    if (loggingOut) return;
    setUserMenuOpen(false);
    setLoggingOut(true);
    try {
      const res = await logoutApi();
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        await alertError(res.message || res.msg || '退出失败，请稍后重试', '退出登录');
        return;
      }
      clearAuth();
      clearMember();
      await alertSuccess(res.message || '注销成功', '退出登录');
      redirectToLogin({ replaceHistory: true });
    } catch {
      await alertError('网络异常，退出失败，请稍后重试', '退出登录');
    } finally {
      setLoggingOut(false);
    }
  };

  const handleOpenChangePassword = () => {
    setUserMenuOpen(false);
    setPasswordModalOpen(true);
  };

  const handleChangePassword = async (payload: { oldPassword: string; newPassword: string }) => {
    if (changingPassword) return;
    setChangingPassword(true);
    try {
      const res = await changePasswordApi({
        old_password: payload.oldPassword,
        // 后端用 password 与 new_password 做「新旧不能相同」校验，需传旧密码
        password: payload.oldPassword,
        new_password: payload.newPassword,
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        const fieldError =
          res.errors?.old_password?.[0] ||
          res.errors?.new_password?.[0] ||
          res.errors?.password?.[0];
        await alertError(fieldError || res.message || res.msg || '修改密码失败，请稍后重试', '修改密码');
        return;
      }
      setPasswordModalOpen(false);
      clearAuth();
      clearMember();
      await alertSuccess(
        res.message ? `${res.message}，请重新登录` : '密码修改成功，请重新登录',
        '修改密码',
      );
      redirectToLogin({ replaceHistory: true });
    } catch {
      await alertError('网络异常，修改密码失败，请稍后重试', '修改密码');
    } finally {
      setChangingPassword(false);
    }
  };

  const isActive = (path: string) => {
    if (path === '/goods') {
      return location.pathname === '/goods' || location.pathname === '/';
    }
    // 采购计划列表/创建/详情：不包含「本月计划」查询页，避免双高亮
    if (path === '/purchase-plan') {
      if (location.pathname === '/purchase-plan') return true;
      if (location.pathname === '/purchase-plan/create') return true;
      return /^\/purchase-plan\/\d+/.test(location.pathname);
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isGroupActive = (group: NavGroup) => group.items.some((item) => isActive(item.path));

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const keyword = searchQuery.trim();
    if (!keyword) return;
    const params = new URLSearchParams();
    if (searchType === 'supplier') params.set('supplier', keyword);
    else params.set('search', keyword);
    navigate(`/goods?${params.toString()}`);
    setSearchQuery('');
    setSearchTypeOpen(false);
  };

  useEffect(() => {
    const onGoods = location.pathname === '/goods' || location.pathname === '/';
    if (!onGoods) return;
    const params = new URLSearchParams(location.search);
    if (params.get('supplier')) setSearchType('supplier');
    else if (params.get('search')) setSearchType('goods');
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      let inDropdown = false;
      Object.values(dropdownRefs.current).forEach((ref) => {
        if (ref && ref.contains(target)) inDropdown = true;
      });
      if (userMenuRef.current && userMenuRef.current.contains(target)) inDropdown = true;
      if (!inDropdown) {
        setOpenDropdown(null);
        setUserMenuOpen(false);
      }
      if (searchTypeRef.current && !searchTypeRef.current.contains(target)) {
        setSearchTypeOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const renderNavLink = (item: NavItem, onClick?: () => void) => {
    const active = isActive(item.path);
    const badge = itemBadge(item.path);
    return (
      <Link
        key={item.path}
        to={item.path}
        className={`flex items-center gap-2.5 px-3 py-2.5 text-sm transition-colors ${
          active
            ? 'bg-primary-50 text-primary-700 font-medium'
            : 'text-foreground-700 hover:bg-background-50 hover:text-foreground-900'
        }`}
        onClick={onClick}
      >
        <div className="w-5 h-5 flex items-center justify-center">
          <i className={item.icon} />
        </div>
        <span className="flex-1">{item.label}</span>
        <Badge count={badge} />
      </Link>
    );
  };

  const renderTopNavItem = (item: NavItem, badge = 0, badgeColor: 'red' | 'blue' = 'red') => {
    const active = isActive(item.path);
    return (
      <Link
        key={item.path}
        to={item.path}
        className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm transition-colors whitespace-nowrap ${
          active
            ? 'bg-primary-50 text-primary-700 font-medium'
            : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-900'
        }`}
      >
        <i className={item.icon} />
        <span>{item.label}</span>
        <Badge count={badge} color={badgeColor} />
      </Link>
    );
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-background-200">
      {/* 科室开启采购控制时，顶部展示采购额度 → 点击进入本月计划 */}
      {deptQuota ? (
        <Link
          to="/purchase-plan/thismonth"
          className="block border-b border-background-100 bg-background-50 px-4 md:px-6 hover:bg-background-100 transition-colors"
          title="查看本月采购计划情况"
        >
          <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-1.5 py-1.5 text-xs">
            <span className="inline-flex items-center gap-1 text-foreground-500 shrink-0">
              <i className="ri-building-line text-primary-500" />
              <span className="font-medium text-foreground-700">
                {currentUser?.department?.title || '当前科室'}
              </span>
            </span>
            <span className="hidden sm:inline text-background-300">|</span>
            <span className="text-foreground-500 whitespace-nowrap">
              采购总额{' '}
              <strong className="text-foreground-800 font-semibold tabular-nums">
                {formatMoney(deptQuota.quota)}
              </strong>
            </span>
            <span className="hidden sm:inline text-background-300">|</span>
            <span className="text-foreground-500 whitespace-nowrap">
              已使用额度{' '}
              <strong className="text-foreground-800 font-semibold tabular-nums">
                {formatMoney(deptQuota.usedAmount)}
              </strong>
            </span>
            <span className="hidden sm:inline text-background-300">|</span>
            <span className="text-foreground-500 whitespace-nowrap">
              采购余额{' '}
              <strong
                className={`font-semibold tabular-nums ${
                  deptQuota.usedRatio >= 95
                    ? 'text-red-600'
                    : deptQuota.usedRatio > 80
                      ? 'text-amber-600'
                      : 'text-primary-600'
                }`}
              >
                {formatMoney(deptQuota.remainAmount)}
              </strong>
            </span>
            <span className="hidden sm:inline text-background-300">|</span>
            <span className="inline-flex items-center gap-1.5 text-foreground-500 whitespace-nowrap">
              已使用比率
              <UsageRatioBar ratio={deptQuota.usedRatio} barClassName="w-16" />
            </span>
            <span className="inline-flex items-center gap-0.5 text-primary-600 shrink-0">
              <span className="hidden sm:inline">本月计划</span>
              <i className="ri-arrow-right-s-line text-sm" />
            </span>
          </div>
        </Link>
      ) : null}

      <div className="flex items-center h-20 px-4 md:px-6 gap-4">
        <Link to="/goods" className="flex items-center gap-2.5 shrink-0 mr-4">
          <img
            src="https://cdn.yyg120.com/Fo_BZtyQ7IYqYrZdSexcrUnft1PI"
            alt="邕宜购"
            className="w-9 h-9 rounded-lg object-cover"
          />
          <div className="hidden sm:block">
            <h1 className="text-sm font-bold text-foreground-900 leading-tight">邕E购</h1>
            <p className="text-[10px] text-foreground-500">物资线上采购平台</p>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {topNavItems.map((item) => renderTopNavItem(item))}

          {/* 采购管理 */}
          {(() => {
            const group = visiblePurchaseGroup;
            const groupActive = isGroupActive(group);
            const isOpen = openDropdown === group.label;
            return (
              <div
                key={group.label}
                className="relative"
                ref={(el) => {
                  dropdownRefs.current[group.label] = el;
                }}
                onMouseEnter={() => setOpenDropdown(group.label)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <button
                  type="button"
                  onClick={() => setOpenDropdown(isOpen ? null : group.label)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm transition-colors whitespace-nowrap cursor-pointer ${
                    groupActive || isOpen
                      ? 'bg-primary-50 text-primary-700 font-medium'
                      : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-900'
                  }`}
                >
                  <i className={group.icon} />
                  <span>{group.label}</span>
                  <Badge count={purchaseGroupBadge} />
                  <i
                    className={`ri-arrow-down-s-line text-xs transition-transform ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {isOpen && <div className="absolute top-full left-0 w-full h-1 z-40" />}

                {isOpen && (
                  <div className="absolute top-[calc(100%+0.25rem)] left-0 w-52 bg-white border border-background-200 rounded-lg shadow-lg z-50 py-1 overflow-hidden">
                    {group.items.map((item) =>
                      renderNavLink(item, () => setOpenDropdown(null)),
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {/* 购物车：采购管理右侧独立菜单 */}
          {renderTopNavItem(cartNavItem, totalCount, 'blue')}

          {/* 工作审批 */}
          {workApprovalItem ? (
            <Link
              to={workApprovalItem.path}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm transition-colors whitespace-nowrap ${
                isActive(workApprovalItem.path)
                  ? 'bg-primary-50 text-primary-700 font-medium'
                  : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-900'
              }`}
            >
              <i className={workApprovalItem.icon} />
              <span>{workApprovalItem.label}</span>
            </Link>
          ) : null}
        </nav>

        <div className="flex-1" />

        <div className="flex items-center gap-2">
          <form
            onSubmit={handleSearch}
            className="hidden lg:flex items-center gap-0 rounded-lg border border-background-200 bg-background-100 focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-100/50 transition-all w-72 xl:w-[28rem] overflow-visible"
          >
            <div className="relative w-20 shrink-0" ref={searchTypeRef}>
              <button
                type="button"
                onClick={() => {
                  setSearchTypeOpen((open) => !open);
                  setUserMenuOpen(false);
                  setOpenDropdown(null);
                }}
                className="flex items-center justify-center gap-0.5 w-full h-full px-1 py-2 text-xs text-foreground-700 hover:bg-background-200/80 transition-colors whitespace-nowrap border-r border-background-200 rounded-l-lg"
              >
                {activeSearchType.label}
                <i
                  className={`ri-arrow-down-s-line text-xs text-foreground-400 transition-transform ${
                    searchTypeOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {searchTypeOpen && (
                <div className="absolute top-[calc(100%+0.25rem)] left-0 w-24 bg-white border border-background-200 rounded-lg shadow-lg z-50 py-1 overflow-hidden">
                  {SEARCH_TYPE_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        setSearchType(opt.value);
                        setSearchTypeOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs transition-colors ${
                        searchType === opt.value
                          ? 'bg-primary-50 text-primary-700 font-medium'
                          : 'text-foreground-700 hover:bg-background-50'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 px-3 py-2 flex-1 min-w-0 overflow-hidden rounded-none">
              <i className="ri-search-line text-sm text-foreground-400 shrink-0" />
              <input
                type="text"
                placeholder={activeSearchType.placeholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 text-sm outline-none bg-transparent min-w-0"
              />
            </div>
            <button
              type="submit"
              className="shrink-0 h-full px-3.5 py-2 bg-primary-500 hover:bg-primary-600 text-white text-xs font-medium transition-colors whitespace-nowrap cursor-pointer rounded-r-lg"
            >
              搜索
            </button>
          </form>

          <Link
            to="/goods"
            className="lg:hidden w-8 h-8 rounded hover:bg-background-100 flex items-center justify-center text-foreground-500 hover:text-foreground-700 transition-colors"
          >
            <i className="ri-search-line text-sm" />
          </Link>

          <div className="relative" ref={userMenuRef}>
            <button
              type="button"
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
            >
              <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                <i className="ri-user-line text-sm text-primary-600" />
              </div>
              <span className="text-sm text-foreground-700 hidden sm:block whitespace-nowrap">
                {displayName}
              </span>
              <i
                className={`ri-arrow-down-s-line text-xs text-foreground-400 hidden sm:block transition-transform ${
                  userMenuOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            {userMenuOpen && (
              <div className="absolute top-[calc(100%+0.25rem)] right-0 w-64 bg-white border border-background-200 rounded-lg shadow-lg z-50 py-1">
                <div className="px-4 py-3 border-b border-background-100">
                  <p className="text-sm font-medium text-foreground-900">{displayName}</p>
                  {displayDept ? (
                    <p className="text-xs text-foreground-500 mt-0.5">{displayDept}</p>
                  ) : null}
                </div>

                {deptQuota ? (
                  <Link
                    to="/purchase-plan/thismonth"
                    onClick={() => setUserMenuOpen(false)}
                    className="block px-4 py-3 border-b border-background-100 space-y-2 hover:bg-background-50 transition-colors"
                    title="查看本月采购计划情况"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[11px] font-semibold text-foreground-500">科室采购额度</p>
                      <span className="inline-flex items-center gap-0.5 text-[11px] text-primary-600">
                        本月计划
                        <i className="ri-arrow-right-s-line text-xs" />
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                      <span className="text-foreground-500">采购总额</span>
                      <span className="text-right font-medium text-foreground-800 tabular-nums">
                        {formatMoney(deptQuota.quota)}
                      </span>
                      <span className="text-foreground-500">已使用额度</span>
                      <span className="text-right font-medium text-foreground-800 tabular-nums">
                        {formatMoney(deptQuota.usedAmount)}
                      </span>
                      <span className="text-foreground-500">采购余额</span>
                      <span
                        className={`text-right font-medium tabular-nums ${
                          deptQuota.usedRatio >= 95
                            ? 'text-red-600'
                            : deptQuota.usedRatio > 80
                              ? 'text-amber-600'
                              : 'text-primary-600'
                        }`}
                      >
                        {formatMoney(deptQuota.remainAmount)}
                      </span>
                      <span className="text-foreground-500">已使用比率</span>
                      <div className="flex justify-end">
                        <UsageRatioBar ratio={deptQuota.usedRatio} barClassName="w-14" />
                      </div>
                    </div>
                  </Link>
                ) : null}

                <button
                  type="button"
                  disabled={changingPassword || loggingOut}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-50 hover:text-foreground-900 transition-colors cursor-pointer disabled:opacity-60"
                  onClick={handleOpenChangePassword}
                >
                  <i className="ri-lock-password-line" />
                  修改密码
                </button>
                <button
                  type="button"
                  disabled={loggingOut}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-500 hover:bg-background-50 hover:text-foreground-700 transition-colors cursor-pointer disabled:opacity-60"
                  onClick={() => void handleLogout()}
                >
                  <i className={loggingOut ? 'ri-loader-4-line animate-spin' : 'ri-logout-box-r-line'} />
                  {loggingOut ? '退出中...' : '退出登录'}
                </button>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => setOpenDropdown(openDropdown === 'mobile' ? null : 'mobile')}
            className="md:hidden w-8 h-8 rounded hover:bg-background-100 flex items-center justify-center text-foreground-500 hover:text-foreground-700 transition-colors"
          >
            <i className={`ri-${openDropdown === 'mobile' ? 'close' : 'menu'}-line text-sm`} />
          </button>
        </div>
      </div>

      <ChangePasswordModal
        isOpen={passwordModalOpen}
        submitting={changingPassword}
        onCancel={() => {
          if (!changingPassword) setPasswordModalOpen(false);
        }}
        onConfirm={handleChangePassword}
      />

      {openDropdown === 'mobile' && (
        <div className="md:hidden border-t border-background-100 bg-white px-4 py-3 space-y-1">
          {topNavItems.map((item) => {
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm ${
                  active
                    ? 'bg-primary-50 text-primary-700 font-medium'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
                onClick={() => setOpenDropdown(null)}
              >
                <i className={item.icon} />
                {item.label}
              </Link>
            );
          })}

          <div>
            <p className="px-3 py-1.5 text-[11px] font-semibold text-foreground-400 uppercase tracking-wider flex items-center gap-1.5">
              <span>{visiblePurchaseGroup.label}</span>
              <Badge count={purchaseGroupBadge} />
            </p>
            {visiblePurchaseGroup.items.map((item) => {
              const active = isActive(item.path);
              const badge = itemBadge(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-sm ${
                    active
                      ? 'bg-primary-50 text-primary-700 font-medium'
                      : 'text-foreground-700 hover:bg-background-100'
                  }`}
                  onClick={() => setOpenDropdown(null)}
                >
                  <i className={item.icon} />
                  <span className="flex-1">{item.label}</span>
                  <Badge count={badge} />
                </Link>
              );
            })}
          </div>

          <Link
            to={cartNavItem.path}
            className={`flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm ${
              isActive(cartNavItem.path)
                ? 'bg-primary-50 text-primary-700 font-medium'
                : 'text-foreground-700 hover:bg-background-100'
            }`}
            onClick={() => setOpenDropdown(null)}
          >
            <i className={cartNavItem.icon} />
            <span className="flex-1">{cartNavItem.label}</span>
            <Badge count={totalCount} color="blue" />
          </Link>

          {workApprovalItem ? (
            <Link
              to={workApprovalItem.path}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm ${
                isActive(workApprovalItem.path)
                  ? 'bg-primary-50 text-primary-700 font-medium'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
              onClick={() => setOpenDropdown(null)}
            >
              <i className={workApprovalItem.icon} />
              {workApprovalItem.label}
            </Link>
          ) : null}
        </div>
      )}
    </header>
  );
}
